import { useSelector } from '@tanstack/react-form';
import {
  Alert,
  Badge,
  Card,
  Dropdown,
  ExternalLink,
  Input,
  NavigationLink,
  Text,
} from '@ui/themed';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { type Platform, useEnv } from '../../../../context/env-context';
import { useHandlers } from '../../../../context/page-context';
import { stringToSlug } from '../../../../utils/slug';
import {
  CUSTOMER_GROUP_BASE_SLUG,
  CUSTOMER_GROUP_NAME_MAX_LENGTH,
  CUSTOMER_GROUP_SLUG_MAX_LENGTH,
} from '../../constants';
import { useCustomerGroupBuilderForm } from '../../hooks/use-customer-group-builder-form';
import { useCustomerGroupTag } from '../../hooks/use-customer-group-tag';
import { useCustomerGroups } from '../../hooks/use-customer-groups';
import type { CustomerGroup } from '../../types/customer-group-response';

function getExistingCustomerGroups(customerGroups: CustomerGroup[], existingSlug?: string) {
  return customerGroups
    .filter((customerGroup) => {
      if (
        customerGroup.parent_slug !== CUSTOMER_GROUP_BASE_SLUG &&
        customerGroup.slug !== CUSTOMER_GROUP_BASE_SLUG
      ) {
        return false;
      }

      return existingSlug ? customerGroup.slug !== existingSlug : true;
    })
    .map((customerGroup) => ({
      value: customerGroup.slug,
      label: `${customerGroup.name}`,
    }));
}

function customerTagsLabelKey(platform: Platform | undefined) {
  switch (platform) {
    case 'magento':
      return 'features:customer_group.components.customer_group_builder_details_card.customer_tags.label_magento' as const;
    case 'wix':
      return 'features:customer_group.components.customer_group_builder_details_card.customer_tags.label_wix' as const;
    default:
      return 'features:customer_group.components.customer_group_builder_details_card.customer_tags.label' as const;
  }
}

export function CustomerGroupBuilderDetailsCard({ isEditing }: { isEditing: boolean }) {
  const { platform } = useEnv();
  const form = useCustomerGroupBuilderForm();
  const { navigationHandler } = useHandlers();
  const { data: customerGroups, isLoading } = useCustomerGroups();

  const slug = useSelector(form.store, (state) => (isEditing ? state.values.slug : ''));
  const hasChildren = useSelector(form.store, (state) => state.values.hasChildren);

  const { tags } = useCustomerGroupTag(slug);
  const tagLink = tags[tags.length - 1]?.link;

  return (
    <Card>
      <Card.Header headingLevel="h2">
        {t('features:customer_group.components.customer_group_builder_details_card.title')}
      </Card.Header>
      <Card.Body>
        <div className="grid w-full gap-3 sm:grid-cols-2">
          <form.Field
            name="name"
            listeners={{
              onChange: ({ value, fieldApi }) => {
                if (!isEditing) {
                  fieldApi.form.setFieldValue('slug', stringToSlug(value));
                }
              },
            }}
          >
            {(field) => (
              <Input
                label={t(
                  'features:customer_group.components.customer_group_builder_details_card.display_name.label',
                )}
                value={field.state.value}
                error={field.state.meta.errors[0]?.message}
                onChange={field.handleChange}
                disabled={isEditing && slug === CUSTOMER_GROUP_BASE_SLUG}
                placeholder={t(
                  'features:customer_group.components.customer_group_builder_details_card.display_name.placeholder',
                )}
                maxLength={CUSTOMER_GROUP_NAME_MAX_LENGTH}
              />
            )}
          </form.Field>
          <form.Field name="slug">
            {(field) => (
              <Input
                label={t(
                  'features:customer_group.components.customer_group_builder_details_card.handle.label',
                )}
                value={field.state.value}
                error={field.state.meta.errors[0]?.message}
                onChange={field.handleChange}
                disabled={isEditing}
                placeholder={t(
                  'features:customer_group.components.customer_group_builder_details_card.handle.placeholder',
                )}
                maxLength={CUSTOMER_GROUP_SLUG_MAX_LENGTH}
              />
            )}
          </form.Field>
        </div>
        <form.Field name="parent_slug">
          {(field) => {
            // hide parent dropdown when editing base customer group
            if (field.state.value === '' || (isEditing && slug === CUSTOMER_GROUP_BASE_SLUG)) {
              return null;
            }

            return (
              <div className="flex flex-col gap-y-2">
                <Text>
                  {t(
                    'features:customer_group.components.customer_group_builder_details_card.configuration.label',
                  )}
                </Text>
                <Dropdown
                  options={getExistingCustomerGroups(customerGroups, isEditing ? slug : undefined)}
                  placeholder={t(
                    'features:customer_group.components.customer_group_builder_details_card.configuration.placeholder',
                  )}
                  disabled={isLoading || hasChildren}
                  value={field.state.value}
                  onChange={field.handleChange}
                />
                <Alert variant="info" headingLevel="h3">
                  <div className="flex flex-col gap-y-2">
                    <span>
                      <Trans
                        ns="features"
                        i18nKey="customer_group.components.customer_group_builder_details_card.alert.label"
                        values={{
                          name:
                            customerGroups?.find((group) => group.slug === field.state.value)
                              ?.name ?? '',
                        }}
                        components={{ strong: <strong /> }}
                      />
                    </span>
                    <NavigationLink
                      onClick={() =>
                        navigationHandler.navigate(`/customers/groups/edit/${field.state.value}`, {
                          newTab: true,
                        })
                      }
                    >
                      {t(
                        'features:customer_group.components.customer_group_builder_details_card.alert.button',
                      )}
                    </NavigationLink>
                  </div>
                </Alert>
              </div>
            );
          }}
        </form.Field>
        {isEditing && (
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-col gap-y-0.5">
              <Text>{t(customerTagsLabelKey(platform), { count: tags.length })}</Text>
              <Text variant="secondary">
                {t(
                  'features:customer_group.components.customer_group_builder_details_card.customer_tags.description',
                )}{' '}
                {tagLink && (
                  <ExternalLink showIcon link={tagLink}>
                    {t(
                      'features:customer_group.components.customer_group_builder_details_card.customer_tags.link.label',
                    )}
                  </ExternalLink>
                )}
              </Text>
            </div>
            <div className="flex flex-row gap-x-2">
              {tags.map(({ tag }) => (
                <Badge key={tag} variant="green">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
